<script lang="ts">
    import type {FieldTypeId, Fond, RecordSearchParams, UUID} from 'typings/portaro.be.types';
    import type {SvelteComponentConstructor} from 'core/types';
    import type {Conjunction} from '../search/search-criteria/criteria.types';
    import type {SearchManager} from 'src/features/search/search-manager/search-manager';
    import {getInjector} from 'core/svelte-context/context';
    import {exists} from 'shared/utils/custom-utils';
    import {RecordGridService} from './services/record-grid.service';
    import {Kind, SearchType} from 'shared/constants/portaro.constants';
    import {onMount} from 'svelte';
    import KpLoadingBlock from 'shared/components/kp-loading/KpLoadingBlock.svelte';
    import KpSearchContext from '../search/kp-search-context/KpSearchContext.svelte';
    import KpStaticSearchContext from 'src/features/search/kp-search-context/KpStaticSearchContext.svelte';
    import KpRecordGridInner from 'src/features/record-grid/KpRecordGridInner.svelte';
    import ControlCell from 'src/features/record-grid/components/cells/ControlCell.svelte';

    export let relatedRecordId: UUID | null = null;
    export let fondOrFondId: Fond | number;
    export let field: FieldTypeId | null = null;
    export let facetRestriction: Conjunction | null = null;
    export let hierarchyFieldTypeId: FieldTypeId | null = null;
    export let relatedFonds: Fond[] | null = null;
    export let includeAddNewRow = true;
    export let includeToolbar = true;
    export let inFullscreenPage = false;
    export let expandedFirstLevel = false;
    export let stickyHeader = true;
    export let controlCellComponent: SvelteComponentConstructor = ControlCell;
    export let searchType: 'record-name' | 'query' = 'query';
    export let injectedSearchManager: SearchManager<any> | null = null;

    const injector = getInjector();
    const recordGridService = injector.getByClass(RecordGridService);

    let selectedFond: Fond;

    onMount(async () => {
        selectedFond = await getFond(fondOrFondId);
    });

    async function loadStaticData(fond: Fond) {
        return Promise.all([
            Promise.resolve(fond),
            recordGridService.loadLayout(fond),
            recordGridService.loadInsertableFonds(fond)
        ]);
    }

    async function getFond(fondOrId: Fond | number): Promise<Fond> {
        if (typeof fondOrId === 'number') {
            return recordGridService.getFond(fondOrId);
        }

        return fondOrId;
    }

    function getContextComponent(): SvelteComponentConstructor {
        return exists(injectedSearchManager) ? KpStaticSearchContext : KpSearchContext;
    }

    function getContextComponentProps(fond: Fond): Record<string, any> {
        return exists(injectedSearchManager) ? {searchManager: injectedSearchManager} : {
            staticParams: createStaticParams(fond),
            localSearch: !inFullscreenPage
        };
    }

    function createStaticParams(rootFond: Fond): RecordSearchParams {
        return {
            kind: [Kind.KIND_RECORD],
            type: SearchType.TYPE_TABLE,
            rootFond: [rootFond],
            recordRelatedRecord: exists(relatedRecordId) ? relatedRecordId : undefined,
            fieldId: exists(field) ? field : undefined,
            facetRestriction: exists(facetRestriction) ? facetRestriction : undefined,
            pageSize: 100
        };
    }

    const handleFondChange = (event: CustomEvent<Fond>) => {
        if(event.detail.id === selectedFond.id) {
            return;
        }

        recordGridService.resetUrlState();
        selectedFond = event.detail;
    };
</script>

{#if !exists(selectedFond)}
    <KpLoadingBlock fillPage/>
{/if}

{#if exists(selectedFond)}
    {#key selectedFond}
        {#await loadStaticData(selectedFond)}
            <KpLoadingBlock fillPage/>
        {:then [fond, gridLayout, insertableFonds]}
            <svelte:component this={getContextComponent()} {...getContextComponentProps(fond)}>
                <KpRecordGridInner {hierarchyFieldTypeId}
                                   {relatedFonds}
                                   {includeAddNewRow}
                                   {includeToolbar}
                                   {inFullscreenPage}
                                   {searchType}
                                   {relatedRecordId}
                                   {expandedFirstLevel}
                                   {controlCellComponent}
                                   {stickyHeader}
                                   {fond}
                                   {gridLayout}
                                   {insertableFonds}
                                   on:fond-change="{handleFondChange}"/>
            </svelte:component>
        {:catch error}
            <div class="text-danger">{JSON.stringify(error, null, 2)}</div>
        {/await}
    {/key}
{/if}